import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import EligibleStudentPicker from '@/pages/extras/EligibleStudentPicker';

/**
 * Мастер создания доп. занятия в ЛК учителя.
 *
 *   1. Дата
 *   2. Карточка (выбор из «своих» карточек — приходит готовый список)
 *   3. Слот + кабинет (опц.)
 *   4. Ученики (для individual — один, для group — список)
 *
 * Без каскадных селектов «предмет → учитель», т.к. учитель видит только
 * свои карточки. Цены snapshot бэк ставит сам из карточки.
 */
export default function TeacherExtraEntryCreateModal({
  defaultDate, lessonCards, times, rooms, onClose, onCreated,
}) {
  const [date, setDate]         = useState(defaultDate || '');
  const [lessonId, setLessonId] = useState(
    lessonCards?.length === 1 ? String(lessonCards[0].id) : ''
  );
  const [timeId, setTimeId]     = useState('');
  const [roomId, setRoomId]     = useState('');
  const [comment, setComment]   = useState('');
  const [picked, setPicked]     = useState([]);

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  // Есть ли несохранённый ввод (дата/занятие могут быть предзаполнены —
  // их не считаем). Нужно, чтобы случайный клик мимо окна / Escape не
  // выкинул из формы без предупреждения.
  const hasUnsavedChanges = () =>
    picked.length > 0 || timeId !== '' || roomId !== '' || comment.trim() !== '';
  const attemptClose = () => {
    if (busy) return;
    if (hasUnsavedChanges() && !window.confirm('Закрыть окно без сохранения данных?')) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') attemptClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // attemptClose/hasUnsavedChanges — из этих состояний; перечислены явно,
    // чтобы Escape-обработчик всегда видел свежие данные.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, busy, picked, timeId, roomId, comment]);

  const lesson = useMemo(
    () => (lessonCards || []).find((l) => String(l.id) === String(lessonId)) || null,
    [lessonCards, lessonId]
  );
  const isIndividual = lesson?.type === 'individual';

  // Индивидуальное и picked > 1 — режем до одного.
  useEffect(() => {
    if (isIndividual && picked.length > 1) setPicked([picked[0]]);
  }, [isIndividual, picked.length]);

  // При смене даты — обнуляем выбор: договоры могут не покрывать новую дату.
  useEffect(() => { setPicked([]); }, [date]);

  const togglePick = (s) => {
    if (isIndividual) { setPicked([s]); return; }
    if (picked.find((p) => p.id === s.id)) {
      setPicked(picked.filter((p) => p.id !== s.id));
    } else {
      setPicked([...picked, s]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!date)     { setError('Выберите дату'); return; }
    if (!lessonId) { setError('Выберите занятие'); return; }
    if (!timeId)   { setError('Выберите временной интервал'); return; }
    if (picked.length === 0) { setError('Добавьте хотя бы одного ученика'); return; }
    if (isIndividual && picked.length !== 1) {
      setError('Для индивидуального занятия нужен ровно один ученик'); return;
    }

    setBusy(true);
    try {
      const res = await api.post('/api/extra-schedule', {
        lessonId:   parseInt(lessonId, 10),
        date,
        timeId:     parseInt(timeId, 10),
        roomId:     roomId ? parseInt(roomId, 10) : null,
        studentIds: picked.map((p) => p.id),
        comment:    comment.trim() || null,
      });
      onCreated?.(res.entry.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) attemptClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Новое занятие</h2>
          <button className="sd-close" onClick={onClose} disabled={busy}><Icon name="x" size={14} /></button>
        </div>

        <form className="sd-edit__form" onSubmit={submit} noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Дата *</label>
              <input
                className="sd-edit__input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Слот *</label>
              <select
                className="sd-edit__select"
                value={timeId}
                onChange={(e) => setTimeId(e.target.value)}
                required
              >
                <option value="">— выберите —</option>
                {(times || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.timeStart || '').slice(0, 5)} — {(t.timeEnd || '').slice(0, 5)}
                  </option>
                ))}
              </select>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Карточка занятия *</label>
              <select
                className="sd-edit__select"
                value={lessonId}
                onChange={(e) => setLessonId(e.target.value)}
                required
              >
                <option value="">— выберите —</option>
                {(lessonCards || []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.subject?.name || '—'} · {l.type === 'group' ? 'Группа' : 'Индив.'}
                    {' · с ученика ' + (Number(l.priceStudent) || 0) + ' ₽'}
                  </option>
                ))}
              </select>
              {lesson && (
                <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>
                  Цены: с ученика {Number(lesson.priceStudent) || 0} ₽,
                  учителю {Number(lesson.priceTeacher) || 0} ₽
                </div>
              )}
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Кабинет</label>
              <select
                className="sd-edit__select"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
              >
                <option value="">— не указан —</option>
                {(rooms || []).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Комментарий</label>
              <input
                className="sd-edit__input"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            {/* Ученики */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">
                Ученики *
                {lesson && (
                  <span style={{ color: 'var(--fg-faint)', fontWeight: 400, marginLeft: 6 }}>
                    {isIndividual ? '(ровно один)' : '(один или несколько)'}
                  </span>
                )}
              </label>

              {picked.length > 0 && (
                <div className="es-picked">
                  {picked.map((p) => (
                    <span key={p.id} className="es-picked__chip">
                      {[p.familyName, p.firstName].filter(Boolean).join(' ')}
                      <span className="muted" style={{ fontSize: 10 }}>· №{p.agreement.number}</span>
                      <button type="button" onClick={() => togglePick(p)} title="Убрать">
                        <Icon name="x" size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {date && (!isIndividual || picked.length === 0) && (
                <EligibleStudentPicker
                  date={date}
                  excludeIds={picked.map((p) => p.id)}
                  onPick={togglePick}
                />
              )}
              {!date && (
                <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>
                  Сначала выберите дату — список доступных учеников зависит от действующих
                  договоров «доп. услуги».
                </div>
              )}
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={busy}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
              {busy ? 'Создаём…' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
